/**
 * Error Handler Utility
 * Provides consistent error message extraction and formatting
 */

export interface ZodIssue {
  code: string
  expected?: string
  received?: string
  path: (string | number)[]
  message: string
}

export interface ZodError {
  issues: ZodIssue[]
  name: string
}

export interface ApiError {
  response?: {
    status?: number
    data?: {
      error?: string | ZodError
      message?: string
      success?: boolean
    }
  }
  message?: string
}

/**
 * Extract user-friendly error message from API error
 */
export function getErrorMessage(error: unknown): string {
  const apiError = error as ApiError
  
  // Log full error for debugging
  console.error('[getErrorMessage] Full error:', error)
  console.error('[getErrorMessage] Error response:', apiError.response)
  
  // Always try to get error from backend response first
  const errorData = apiError.response?.data?.error
  
  // Handle Zod validation errors
  if (errorData && typeof errorData === 'object' && 'issues' in errorData) {
    const zodError = errorData as ZodError
    // Format Zod issues into user-friendly message
    const issues = zodError.issues.map(issue => {
      const field = issue.path.join('.')
      return `${field}: ${issue.message}`
    }).join('; ')
    return `Validation error: ${issues}`
  }
  
  // Handle string error messages
  if (typeof errorData === 'string') {
    return errorData
  }
  
  if (apiError.response?.data?.message) {
    return apiError.response.data.message
  }
  
  // Fallback based on status code (only if no backend message)
  const status = apiError.response?.status
  
  switch (status) {
    case 400:
      // For validation errors, backend should provide specific message
      // Only use generic if no message provided
      return 'Invalid request. Please check your input.'
    case 401:
      return 'Authentication required. Please log in.'
    case 403:
      return 'You don\'t have permission to perform this action.'
    case 404:
      return 'Resource not found.'
    case 409:
      return 'This resource already exists.'
    case 429:
      return 'Too many requests. Please try again later.'
    case 500:
      // For 500 errors, try to show any available error info
      const errorData = apiError.response?.data as any
      if (errorData && typeof errorData === 'object') {
        // Try to extract any error information
        const errorInfo = errorData.error || errorData.message || JSON.stringify(errorData)
        return `Server error: ${errorInfo}`
      }
      return 'Server error. Please try again later.'
    case 503:
      return 'Service temporarily unavailable. Please try again later.'
    default:
      // Use error message if available
      if (apiError.message) {
        return apiError.message
      }
      return 'An error occurred. Please try again.'
  }
}

/**
 * Check if error is a validation error (400)
 */
export function isValidationError(error: unknown): boolean {
  const apiError = error as ApiError
  return apiError.response?.status === 400
}

/**
 * Check if error is an authentication error (401)
 */
export function isAuthError(error: unknown): boolean {
  const apiError = error as ApiError
  return apiError.response?.status === 401
}

/**
 * Check if error is a permission error (403)
 */
export function isPermissionError(error: unknown): boolean {
  const apiError = error as ApiError
  return apiError.response?.status === 403
}

/**
 * Check if error is a not found error (404)
 */
export function isNotFoundError(error: unknown): boolean {
  const apiError = error as ApiError
  return apiError.response?.status === 404
}

/**
 * Check if error is a rate limit error (429)
 */
export function isRateLimitError(error: unknown): boolean {
  const apiError = error as ApiError
  return apiError.response?.status === 429
}

/**
 * Check if error is a server error (5xx)
 */
export function isServerError(error: unknown): boolean {
  const apiError = error as ApiError
  const status = apiError.response?.status
  return status !== undefined && status >= 500 && status < 600
}
